// Message bubbles + thinking indicator + empty state.
// Bubble styling switches on data-style (rounded / square / minimal)
// which the parent reads from tweaks.bubble.

import { Icon } from "../../icons";
import { formatMd } from "../../lib/format-md";
import type { Tweaks } from "../../lib/use-tweaks";
import type { ChatMessage } from "../../lib/use-chat";

export function Message({ m, bubble, idx }: { m: ChatMessage; bubble: Tweaks["bubble"]; idx?: number }) {
  const hasImages = m.images && m.images.length > 0;
  const hasVisibleText = !!(m.text && m.text.trim().length > 0);
  // Hide assistant bubbles that have no visible content. Qwen-family
  // models stream a couple whitespace-only tokens before deciding to
  // call a tool — without this guard those render as ghost empty
  // bubbles below the user message.
  if (m.role === "assistant" && !hasImages && !hasVisibleText) return null;
  const hasText = hasVisibleText;
  return (
    <div className={`msg ${m.role}`} data-style={bubble} data-msg-idx={idx}>
      {m.role === "assistant" && bubble !== "minimal" && (
        <div className="ai-av"><Icon name="sparkle" size={16} /></div>
      )}
      <div className="role-tag">{m.role === "user" ? "You" : "Vulture Chat"}</div>
      <div className="bubble">
        {hasImages && (
          <div className="bubble-images">
            {m.images!.map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                <img src={src} alt="" />
              </a>
            ))}
          </div>
        )}
        {hasText && (
          <div
            class={hasImages ? "bubble-text bubble-text-after-images" : "bubble-text"}
            dangerouslySetInnerHTML={{ __html: formatMd(m.text) }}
          />
        )}
      </div>
    </div>
  );
}

export function ThinkingBubble({ bubble }: { bubble: Tweaks["bubble"] }) {
  return (
    <div className="msg assistant" data-style={bubble}>
      {bubble !== "minimal" && (
        <div className="ai-av"><Icon name="sparkle" size={16} /></div>
      )}
      <div className="role-tag">Vulture Chat</div>
      <div className="bubble">
        <span className="thinking-dots"><span /><span /><span /></span>
      </div>
    </div>
  );
}

interface EmptyChatProps {
  onTry: (prompt: string) => void;
  suggestions: string[];
}

export function EmptyChat({ onTry, suggestions }: EmptyChatProps) {
  return (
    <div style={{ textAlign: "center", padding: "10vh 20px" }}>
      <div
        className="ai-av"
        style={{ margin: "0 auto 16px", width: 56, height: 56, borderRadius: 16 }}
      >
        <Icon name="sparkle" size={24} />
      </div>
      <h2 className="h-display" style={{ fontSize: 32, margin: 0, letterSpacing: "-0.02em" }}>
        What's on your{" "}
        <span className="h-italic" style={{ color: "var(--accent)" }}>mind?</span>
      </h2>
      <p style={{ color: "var(--text-mute)", marginTop: 8 }}>A fresh thread. Tell it anything.</p>
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 8,
          justifyContent: "center",
          maxWidth: 560, margin: "20px auto 0",
        }}
      >
        {suggestions.map((s) => (
          <button key={s} className="chip" onClick={() => onTry(s)} style={{ cursor: "default" }}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
